const RELATIVE_API_BASE = '/api';

const hasWindow = typeof window !== 'undefined' && typeof window.location !== 'undefined';

const DEFAULT_API_BASE = hasWindow && window.location.hostname === 'localhost'
  ? 'http://127.0.0.1:8000/api'
  : RELATIVE_API_BASE;

function normalizeApiBaseUrl(raw: string | undefined): string {
  if (!raw) {
    return DEFAULT_API_BASE;
  }

  const trimmed = raw.trim();

  if (trimmed === '') {
    return DEFAULT_API_BASE;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed.replace(/\/+$/, '');
  }

  if (trimmed.startsWith('/')) {
    return trimmed.replace(/\/+$/, '') || '/';
  }

  return `https://${trimmed}`.replace(/\/+$/, '');
}

const configuredApiBase = (import.meta as ImportMeta).env?.VITE_API_BASE_URL;

export const API_BASE_URL = normalizeApiBaseUrl(configuredApiBase);
export const API_FALLBACK_BASE_URL = RELATIVE_API_BASE;

type AuthTokenProvider = () => Promise<string | null>;
type UserObjectIdProvider = () => string | null;

let authTokenProvider: AuthTokenProvider | null = null;
let userObjectIdProvider: UserObjectIdProvider | null = null;

export function setAuthTokenProvider(provider: AuthTokenProvider | null): void {
  authTokenProvider = provider;
}

export function setUserObjectIdProvider(
  provider: UserObjectIdProvider | null
): void {
  userObjectIdProvider = provider;
}

export interface HealthResponse {
  status: string;
  timestamp: string;
}

export interface Item {
  id: number;
  name: string;
  created_at: string;
}

export interface ConnectionTestResponse {
  message: string;
  ok: boolean;
  timestamp: string;
}

export interface TickerSearchResult {
  symbol: string;
  shortName?: string | null;
  longName?: string | null;
  type?: string | null;
  exchange?: string | null;
  score?: number | null;
}

export interface QuoteSummary {
  symbol: string;
  currency?: string | null;
  shortName?: string | null;
  longName?: string | null;
  regularMarketPrice?: number | null;
  regularMarketChange?: number | null;
  regularMarketChangePercent?: number | null;
  regularMarketPreviousClose?: number | null;
  regularMarketOpen?: number | null;
  regularMarketDayLow?: number | null;
  regularMarketDayHigh?: number | null;
  fiftyTwoWeekLow?: number | null;
  fiftyTwoWeekHigh?: number | null;
  marketCap?: number | null;
  regularMarketVolume?: number | null;
}

export interface OptionsContract {
  contractSymbol: string;
  strike: number;
  lastPrice?: number | null;
  bid?: number | null;
  ask?: number | null;
  change?: number | null;
  percentChange?: number | null;
  volume?: number | null;
  openInterest?: number | null;
  impliedVolatility?: number | null;
  inTheMoney?: boolean | null;
}

export interface OptionsChain {
  ticker: string;
  availableExpirations: number[];
  selectedExpiration: number;
  calls: OptionsContract[];
  puts: OptionsContract[];
  maxPainSeries?: Array<{ expiration: number; strike: number | null }>;
}

export interface MaxPainSummary {
  symbol: string;
  strike: number;
  source: string;
  asOf: string;
}

export interface OptionsSnapshot {
  ticker: string;
  quote: QuoteSummary;
  chain: OptionsChain;
  maxPain: MaxPainSummary | null;
}

export interface PriceHistoryPoint {
  timestamp: string;
  close: number | null;
}

export interface PriceHistoryResponse {
  ticker: string;
  period: string;
  points: PriceHistoryPoint[];
}

export interface UnifiedConnectionSummary {
  id: string;
  institutionId?: string | null;
  institutionName?: string | null;
  status: string;
  lastSyncedAt?: string | null;
}

export interface UnifiedAccountSummary {
  id: string;
  connectionId: string;
  institutionName?: string | null;
  name: string;
  mask?: string | null;
  accountNumber?: string | null;
  accountType: string;
  accountSubtype?: string | null;
  currency?: string | null;
  currentBalance?: number | null;
  availableBalance?: number | null;
  lastSyncedAt?: string | null;
}

export interface UnifiedAccountsSnapshot {
  netWorth: number | null;
  cashTotal: number | null;
  investmentTotal: number | null;
  debtTotal: number | null;
  lastSyncedAt: string | null;
  connections: UnifiedConnectionSummary[];
  accounts: UnifiedAccountSummary[];
}

export interface UnifiedLinkTokenResponse {
  linkToken: string;
  expiration: string;
}

export interface UnifiedLinkTokenRequestPayload {
  connectionId?: string;
  oauthStateId?: string;
}

export interface UnifiedInstitutionMetadata {
  institutionId?: string | null;
  name?: string | null;
  url?: string | null;
  logoUrl?: string | null;
  primaryColor?: string | null;
}

export interface UnifiedAccountMetadata {
  accountId: string;
  name?: string | null;
  mask?: string | null;
  type?: string | null;
  subtype?: string | null;
}

export interface UnifiedExchangePayload {
  publicToken: string;
  institution?: UnifiedInstitutionMetadata;
  accounts?: UnifiedAccountMetadata[];
}

export interface UnifiedTransactionRecord {
  id: string;
  accountId: string;
  externalId: string;
  amount: number;
  isoCurrencyCode?: string | null;
  merchantName?: string | null;
  description?: string | null;
  category?: string | null;
  merchantCategoryCode?: string | null;
  pending: boolean;
  postedAt?: string | null;
  authorizedAt?: string | null;
  paymentChannel?: string | null;
}

export interface UnifiedTransactionsResponse {
  account: UnifiedAccountSummary;
  transactions: UnifiedTransactionRecord[];
}

export interface UnifiedPositionRecord {
  id: string;
  accountId: string;
  symbol?: string | null;
  name?: string | null;
  quantity?: number | null;
  price?: number | null;
  institutionValue?: number | null;
  currency?: string | null;
  costBasis?: number | null;
  unrealizedGain?: number | null;
  asOf?: string | null;
}

export interface UnifiedPositionsResponse {
  account: UnifiedAccountSummary;
  positions: UnifiedPositionRecord[];
}
export type TaskUrgency = 'urgent' | 'not_urgent' | 'habit' | 'backlog';
export type TaskImportance = 'important' | 'not_important' | 'move_the_needle';
export type TaskDifficulty = 'flow' | 'quick' | 'easy' | 'personal';
export type TaskStatus = 'archived' | 'backlog' | 'ongoing' | 'active' | 'completed';

export interface RelatedEntitySummary {
  id: number;
  name: string;
}

export interface Task {
  id: number;
  name: string;
  done: boolean;
  attemptWithAi: boolean;
  createdAt: string;
  urgency: TaskUrgency;
  importance: TaskImportance;
  difficulty: TaskDifficulty;
  status: TaskStatus;
  timeStarted: string | null;
  timeFinished: string | null;
  timeEstimateMinutes: number | null;
  projects: RelatedEntitySummary[];
  buckets: RelatedEntitySummary[];
  parentTask?: RelatedEntitySummary | null;
  children?: RelatedEntitySummary[];
}

export type TaskFilter = 'today' | 'tomorrow' | 'this_week' | 'overdue';

export interface TaskCreatePayload {
  name: string;
  done?: boolean;
  attemptWithAi?: boolean;
  urgency?: TaskUrgency;
  importance?: TaskImportance;
  difficulty?: TaskDifficulty;
  status?: TaskStatus;
  timeStarted?: string | null;
  timeFinished?: string | null;
  timeEstimateMinutes?: number | null;
  parentTaskId?: number | null;
  projectIds?: number[];
  bucketIds?: number[];
  projectId?: number | null;
  bucketId?: number | null;
}

export interface TaskUpdatePayload {
  name?: string;
  done?: boolean;
  attemptWithAi?: boolean;
  urgency?: TaskUrgency;
  importance?: TaskImportance;
  difficulty?: TaskDifficulty;
  status?: TaskStatus;
  timeStarted?: string | null;
  timeFinished?: string | null;
  timeEstimateMinutes?: number | null;
  parentTaskId?: number | null;
  projectIds?: number[];
  bucketIds?: number[];
  projectId?: number | null;
  bucketId?: number | null;
}

export interface Project {
  id: number;
  name: string;
  status: TaskStatus;
  deadline: string | null;
  parentProject: RelatedEntitySummary | null;
  subprojects: RelatedEntitySummary[];
  tasks: RelatedEntitySummary[];
  buckets: RelatedEntitySummary[];
}

export interface ProjectCreatePayload {
  name: string;
  status?: TaskStatus;
  deadline?: string | null;
  parentProjectId?: number | null;
  taskIds?: number[];
  bucketIds?: number[];
}

export interface ProjectUpdatePayload {
  name?: string;
  status?: TaskStatus;
  deadline?: string | null;
  parentProjectId?: number | null;
  taskIds?: number[];
  bucketIds?: number[];
}

export interface Bucket {
  id: number;
  name: string;
  createdAt: string;
  projects: RelatedEntitySummary[];
  tasks: RelatedEntitySummary[];
}

export interface BucketCreatePayload {
  name: string;
  projectIds?: number[];
  taskIds?: number[];
}

export interface BucketUpdatePayload {
  name?: string;
  projectIds?: number[];
  taskIds?: number[];
}

export interface KingdomPlayerState {
  playerId: string;
  name: string;
  wordSubmitted: boolean;
}

export interface KingdomWordState {
  wordId: string;
  text: string | null;
  isYourWord: boolean;
  guessed: boolean;
  guessedByPlayerId: string | null;
  ownerPlayerId: string | null;
  dictionaryVerified: boolean | null;
  dictionaryDefinition: string | null;
  dictionaryUrl: string | null;
}

export interface KingdomGameState {
  code: string;
  status: 'lobby' | 'started' | 'completed';
  yourPlayerId: string;
  players: KingdomPlayerState[];
  words: KingdomWordState[];
  readingOrder: string[];
  startedAt: string | null;
  completedAt: string | null;
}

export interface KingdomJoinResponse {
  code: string;
  playerId: string;
  playerToken: string;
}

export interface KingdomGuessResponse {
  correct: boolean;
  state: KingdomGameState;
}

export interface SortingHatResult {
  prompt: string;
  response: string;
  audio_base64?: string | null;
}

export interface SortingHatAssignment {
  name: string;
  house: string;
  reason: string;
}

export interface SortingHatAssignmentsResponse {
  assignments: SortingHatAssignment[];
}

export interface SortingHatSelectionResponse {
  selected_name: string | null;
}

export interface SortingHatControllerState {
  order: string[];
  current_index: number;
  state: string;
}

export interface SortingHatStepResponse {
  audio_base64: string;
  prompt_text?: string | null;
  next_state?: string | null;
  listen?: boolean | null;
}

export interface SortingHatSpeakResponse {
  audio_base64: string;
}

interface RawKingdomPlayerState {
  player_id: string;
  name: string;
  word_submitted: boolean;
}

interface RawKingdomWordState {
  word_id: string;
  text: string | null;
  is_your_word: boolean;
  guessed: boolean;
  guessed_by_player_id: string | null;
  owner_player_id: string | null;
  dictionary_verified: boolean | null;
  dictionary_definition: string | null;
  dictionary_url: string | null;
}

interface RawKingdomGameState {
  code: string;
  status: 'lobby' | 'started' | 'completed';
  your_player_id: string;
  players: RawKingdomPlayerState[];
  words: RawKingdomWordState[];
  reading_order: string[];
  started_at: string | null;
  completed_at: string | null;
}

function mapKingdomState(raw: RawKingdomGameState): KingdomGameState {
  return {
    code: raw.code,
    status: raw.status,
    yourPlayerId: raw.your_player_id,
    players: raw.players.map((player) => ({
      playerId: player.player_id,
      name: player.name,
      wordSubmitted: player.word_submitted
    })),
    words: raw.words.map((word) => ({
      wordId: word.word_id,
      text: word.text,
      isYourWord: word.is_your_word,
      guessed: word.guessed,
      guessedByPlayerId: word.guessed_by_player_id,
      ownerPlayerId: word.owner_player_id,
      dictionaryVerified: word.dictionary_verified,
      dictionaryDefinition: word.dictionary_definition,
      dictionaryUrl: word.dictionary_url
    })),
    readingOrder: raw.reading_order,
    startedAt: raw.started_at,
    completedAt: raw.completed_at
  };
}

interface FetchAttempt {
  url: string;
  response?: Response;
  error?: Error;
}

function buildRequestUrl(base: string, path: string): string {
  const normalizedBase = base.replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${normalizedBase}${normalizedPath}`;
}

function isAbsoluteUrl(candidate: string): boolean {
  return /^https?:\/\//i.test(candidate);
}

async function tryFetch(base: string, path: string, init?: RequestInit): Promise<FetchAttempt> {
  const url = buildRequestUrl(base, path);

  try {
    let authHeaders: Record<string, string> = {};
    let userHeaders: Record<string, string> = {};

    if (authTokenProvider) {
      const token = await authTokenProvider();

      if (token) {
        authHeaders = { Authorization: `Bearer ${token}` };
      }
    }

    if (userObjectIdProvider) {
      try {
        const userObjectId = userObjectIdProvider();

        if (userObjectId) {
          userHeaders = { 'X-User-Object-Id': userObjectId };
        }
      } catch (err) {
        console.error('Failed to resolve user object ID for request', err);
      }
    }

    const headers = new Headers(init?.headers ?? {});

    Object.entries(authHeaders).forEach(([key, value]) => {
      headers.set(key, value);
    });

    Object.entries(userHeaders).forEach(([key, value]) => {
      headers.set(key, value);
    });

    if (!headers.has('Accept')) {
      headers.set('Accept', 'application/json');
    }

    const bodyProvided = init?.body !== undefined && init?.body !== null;
    const isFormBody = typeof FormData !== 'undefined' && bodyProvided && init?.body instanceof FormData;

    if (bodyProvided && !isFormBody && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    const requestInit: RequestInit = {
      ...init,
      headers
    };

    const response = await fetch(url, requestInit);
    return { url, response };
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err));
    return { url, error };
  }
}

async function createHttpError(attempt: FetchAttempt): Promise<Error> {
  const response = attempt.response;

  if (!response) {
    return attempt.error ?? new Error(`Request to ${attempt.url} failed.`);
  }

  let details = '';

  try {
    details = (await response.text()).trim();
  } catch (err) {
    details = err instanceof Error ? err.message : '';
  }

  const statusText = response.statusText || 'Request failed';
  const description = details || statusText;
  return new Error(`Request to ${attempt.url} failed with status ${response.status}: ${description}`);
}

function shouldRetryWithFallback(attempt: FetchAttempt, base: string): boolean {
  return Boolean(
    attempt.error &&
      attempt.response === undefined &&
      isAbsoluteUrl(base) &&
      base !== RELATIVE_API_BASE
  );
}

function buildFallbackError(primary: FetchAttempt, fallback: FetchAttempt): Error {
  const primaryMessage = primary.error?.message ?? 'Unknown error';
  if (fallback.response) {
    return new Error(
      `Primary request to ${primary.url} failed: ${primaryMessage}. ` +
        `Fallback request to ${fallback.url} returned status ${fallback.response.status}.`
    );
  }

  const fallbackMessage = fallback.error?.message ?? 'Unknown error';
  return new Error(
    `Primary request to ${primary.url} failed: ${primaryMessage}. ` +
      `Fallback request to ${fallback.url} failed: ${fallbackMessage}.`
  );
}

interface FinanceSearchResponse {
  query: string;
  results: TickerSearchResult[];
}

interface FinanceSnapshotResponse {
  ticker: string;
  quote: QuoteSummary;
  chain: OptionsChain;
  maxPain: MaxPainSummary | null;
}

async function parseJsonResponse<T>(attempt: FetchAttempt): Promise<T> {
  if (!attempt.response) {
    throw attempt.error ?? new Error(`Request to ${attempt.url} failed.`);
  }

  try {
    return (await attempt.response.json()) as T;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unable to parse JSON response.';
    throw new Error(`Failed to parse JSON from ${attempt.url}: ${message}`);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const primaryAttempt = await tryFetch(API_BASE_URL, path, init);

  if (primaryAttempt.response) {
    if (!primaryAttempt.response.ok) {
      throw await createHttpError(primaryAttempt);
    }

    return parseJsonResponse<T>(primaryAttempt);
  }

  if (!shouldRetryWithFallback(primaryAttempt, API_BASE_URL)) {
    throw primaryAttempt.error ?? new Error(`Request to ${primaryAttempt.url} failed.`);
  }

  console.warn(
    `Primary API request to ${primaryAttempt.url} failed with '${primaryAttempt.error?.message}'. ` +
      `Retrying via ${RELATIVE_API_BASE} proxy.`
  );

  const fallbackAttempt = await tryFetch(RELATIVE_API_BASE, path, init);

  if (fallbackAttempt.response) {
    if (!fallbackAttempt.response.ok) {
      throw await createHttpError(fallbackAttempt);
    }

    return parseJsonResponse<T>(fallbackAttempt);
  }

  throw buildFallbackError(primaryAttempt, fallbackAttempt);
}

async function requestVoid(path: string, init?: RequestInit): Promise<void> {
  const primaryAttempt = await tryFetch(API_BASE_URL, path, init);

  if (primaryAttempt.response) {
    if (!primaryAttempt.response.ok) {
      throw await createHttpError(primaryAttempt);
    }

    return;
  }

  if (!shouldRetryWithFallback(primaryAttempt, API_BASE_URL)) {
    throw primaryAttempt.error ?? new Error(`Request to ${primaryAttempt.url} failed.`);
  }

  console.warn(
    `Primary API request to ${primaryAttempt.url} failed with '${primaryAttempt.error?.message}'. ` +
      `Retrying via ${RELATIVE_API_BASE} proxy.`
  );

  const fallbackAttempt = await tryFetch(RELATIVE_API_BASE, path, init);

  if (fallbackAttempt.response) {
    if (!fallbackAttempt.response.ok) {
      throw await createHttpError(fallbackAttempt);
    }

    return;
  }

  throw buildFallbackError(primaryAttempt, fallbackAttempt);
}

interface RawTask {
  id: number;
  name: string;
  done: boolean;
  attempt_with_ai: boolean;
  created_at: string;
  urgency: TaskUrgency;
  importance: TaskImportance;
  difficulty: TaskDifficulty;
  status: TaskStatus;
  time_started: string | null;
  time_finished: string | null;
  time_estimate: number | null;
  projects: RelatedEntitySummary[];
  buckets: RelatedEntitySummary[];
  parent_task?: RelatedEntitySummary | null;
  children?: RelatedEntitySummary[];
}

interface RawProject {
  id: number;
  name: string;
  status: TaskStatus;
  deadline: string | null;
  parent_project: RelatedEntitySummary | null;
  subprojects: RelatedEntitySummary[];
  tasks: RelatedEntitySummary[];
  buckets: RelatedEntitySummary[];
}

interface RawBucket {
  id: number;
  name: string;
  created_at: string;
  projects: RelatedEntitySummary[];
  tasks: RelatedEntitySummary[];
}

function mapTask(raw: RawTask): Task {
  const task: Task = {
    id: raw.id,
    name: raw.name,
    done: raw.done,
    attemptWithAi: raw.attempt_with_ai,
    createdAt: raw.created_at,
    urgency: raw.urgency,
    importance: raw.importance,
    difficulty: raw.difficulty,
    status: raw.status,
    timeStarted: raw.time_started,
    timeFinished: raw.time_finished,
    timeEstimateMinutes: raw.time_estimate,
    projects: raw.projects,
    buckets: raw.buckets
  };

  if (raw.parent_task !== undefined) {
    task.parentTask = raw.parent_task ?? null;
  }

  if (raw.children !== undefined) {
    task.children = raw.children;
  }

  return task;
}

function mapProject(raw: RawProject): Project {
  return {
    id: raw.id,
    name: raw.name,
    status: raw.status,
    deadline: raw.deadline,
    parentProject: raw.parent_project,
    subprojects: raw.subprojects,
    tasks: raw.tasks,
    buckets: raw.buckets
  };
}

function mapBucket(raw: RawBucket): Bucket {
  return {
    id: raw.id,
    name: raw.name,
    createdAt: raw.created_at,
    projects: raw.projects,
    tasks: raw.tasks
  };
}

interface RawUnifiedAccountSummary {
  id: string;
  connectionId: string;
  institutionName?: string | null;
  name: string;
  mask?: string | null;
  accountNumber?: string | null;
  accountType: string;
  accountSubtype?: string | null;
  currency?: string | null;
  currentBalance?: number | string | null;
  availableBalance?: number | string | null;
  lastSyncedAt?: string | null;
}

interface RawUnifiedAccountsSnapshot {
  netWorth?: number | string | null;
  cashTotal?: number | string | null;
  investmentTotal?: number | string | null;
  debtTotal?: number | string | null;
  lastSyncedAt?: string | null;
  connections: UnifiedConnectionSummary[];
  accounts: RawUnifiedAccountSummary[];
}

interface RawUnifiedTransactionRecord {
  id: string;
  accountId: string;
  externalId: string;
  amount: number | string;
  isoCurrencyCode?: string | null;
  merchantName?: string | null;
  description?: string | null;
  category?: string | null;
  merchantCategoryCode?: string | null;
  pending: boolean;
  postedAt?: string | null;
  authorizedAt?: string | null;
  paymentChannel?: string | null;
}

interface RawUnifiedTransactionsResponse {
  account: RawUnifiedAccountSummary;
  transactions: RawUnifiedTransactionRecord[];
}

interface RawUnifiedPositionRecord {
  id: string;
  accountId: string;
  symbol?: string | null;
  name?: string | null;
  quantity?: number | string | null;
  price?: number | string | null;
  institutionValue?: number | string | null;
  currency?: string | null;
  costBasis?: number | string | null;
  unrealizedGain?: number | string | null;
  asOf?: string | null;
}

interface RawUnifiedPositionsResponse {
  account: RawUnifiedAccountSummary;
  positions: RawUnifiedPositionRecord[];
}

function toNumber(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function mapUnifiedAccount(raw: RawUnifiedAccountSummary): UnifiedAccountSummary {
  return {
    id: raw.id,
    connectionId: raw.connectionId,
    institutionName: raw.institutionName ?? null,
    name: raw.name,
    mask: raw.mask ?? null,
    accountNumber: raw.accountNumber ?? null,
    accountType: raw.accountType,
    accountSubtype: raw.accountSubtype ?? null,
    currency: raw.currency ?? null,
    currentBalance: toNumber(raw.currentBalance),
    availableBalance: toNumber(raw.availableBalance),
    lastSyncedAt: raw.lastSyncedAt ?? null
  };
}

function mapUnifiedAccountsSnapshot(raw: RawUnifiedAccountsSnapshot): UnifiedAccountsSnapshot {
  return {
    netWorth: toNumber(raw.netWorth),
    cashTotal: toNumber(raw.cashTotal),
    investmentTotal: toNumber(raw.investmentTotal),
    debtTotal: toNumber(raw.debtTotal),
    lastSyncedAt: raw.lastSyncedAt ?? null,
    connections: raw.connections ?? [],
    accounts: (raw.accounts ?? []).map(mapUnifiedAccount)
  };
}

function mapUnifiedTransaction(raw: RawUnifiedTransactionRecord): UnifiedTransactionRecord {
  return {
    id: raw.id,
    accountId: raw.accountId,
    externalId: raw.externalId,
    amount: toNumber(raw.amount) ?? 0,
    isoCurrencyCode: raw.isoCurrencyCode ?? null,
    merchantName: raw.merchantName ?? null,
    description: raw.description ?? null,
    category: raw.category ?? null,
    merchantCategoryCode: raw.merchantCategoryCode ?? null,
    pending: raw.pending,
    postedAt: raw.postedAt ?? null,
    authorizedAt: raw.authorizedAt ?? null,
    paymentChannel: raw.paymentChannel ?? null
  };
}

function mapUnifiedTransactionsResponse(raw: RawUnifiedTransactionsResponse): UnifiedTransactionsResponse {
  return {
    account: mapUnifiedAccount(raw.account),
    transactions: raw.transactions.map(mapUnifiedTransaction)
  };
}

function mapUnifiedPosition(raw: RawUnifiedPositionRecord): UnifiedPositionRecord {
  return {
    id: raw.id,
    accountId: raw.accountId,
    symbol: raw.symbol ?? null,
    name: raw.name ?? null,
    quantity: toNumber(raw.quantity),
    price: toNumber(raw.price),
    institutionValue: toNumber(raw.institutionValue),
    currency: raw.currency ?? null,
    costBasis: toNumber(raw.costBasis),
    unrealizedGain: toNumber(raw.unrealizedGain),
    asOf: raw.asOf ?? null
  };
}

function mapUnifiedPositionsResponse(raw: RawUnifiedPositionsResponse): UnifiedPositionsResponse {
  return {
    account: mapUnifiedAccount(raw.account),
    positions: raw.positions.map(mapUnifiedPosition)
  };
}

function buildTaskPayload(payload: TaskCreatePayload | TaskUpdatePayload): Record<string, unknown> {
  const body: Record<string, unknown> = {};

  if ('name' in payload && payload.name !== undefined) {
    body.name = payload.name;
  }
  if ('done' in payload && payload.done !== undefined) {
    body.done = payload.done;
  }
  if ('attemptWithAi' in payload && payload.attemptWithAi !== undefined) {
    body.attempt_with_ai = payload.attemptWithAi;
  }
  if ('urgency' in payload && payload.urgency !== undefined) {
    body.urgency = payload.urgency;
  }
  if ('importance' in payload && payload.importance !== undefined) {
    body.importance = payload.importance;
  }
  if ('difficulty' in payload && payload.difficulty !== undefined) {
    body.difficulty = payload.difficulty;
  }
  if ('status' in payload && payload.status !== undefined) {
    body.status = payload.status;
  }
  if ('timeStarted' in payload && payload.timeStarted !== undefined) {
    body.time_started = payload.timeStarted;
  }
  if ('timeFinished' in payload && payload.timeFinished !== undefined) {
    body.time_finished = payload.timeFinished;
  }
  if ('timeEstimateMinutes' in payload && payload.timeEstimateMinutes !== undefined) {
    body.time_estimate = payload.timeEstimateMinutes;
  }
  if ('parentTaskId' in payload && payload.parentTaskId !== undefined) {
    body.parent_task_id = payload.parentTaskId;
  }
  if ('projectIds' in payload && payload.projectIds !== undefined) {
    body.project_ids = payload.projectIds;
  }
  if ('bucketIds' in payload && payload.bucketIds !== undefined) {
    body.bucket_ids = payload.bucketIds;
  }
  if ('projectId' in payload && payload.projectId !== undefined) {
    body.project_id = payload.projectId;
  }
  if ('bucketId' in payload && payload.bucketId !== undefined) {
    body.bucket_id = payload.bucketId;
  }

  return body;
}

function buildProjectPayload(payload: ProjectCreatePayload | ProjectUpdatePayload): Record<string, unknown> {
  const body: Record<string, unknown> = {};

  if ('name' in payload && payload.name !== undefined) {
    body.name = payload.name;
  }
  if ('status' in payload && payload.status !== undefined) {
    body.status = payload.status;
  }
  if ('deadline' in payload && payload.deadline !== undefined) {
    body.deadline = payload.deadline;
  }
  if ('parentProjectId' in payload && payload.parentProjectId !== undefined) {
    body.parent_project_id = payload.parentProjectId;
  }
  if ('taskIds' in payload && payload.taskIds !== undefined) {
    body.task_ids = payload.taskIds;
  }
  if ('bucketIds' in payload && payload.bucketIds !== undefined) {
    body.bucket_ids = payload.bucketIds;
  }

  return body;
}

function buildBucketPayload(payload: BucketCreatePayload | BucketUpdatePayload): Record<string, unknown> {
  const body: Record<string, unknown> = {};

  if ('name' in payload && payload.name !== undefined) {
    body.name = payload.name;
  }
  if ('projectIds' in payload && payload.projectIds !== undefined) {
    body.project_ids = payload.projectIds;
  }
  if ('taskIds' in payload && payload.taskIds !== undefined) {
    body.task_ids = payload.taskIds;
  }

  return body;
}

export const api = {
  health: () => request<HealthResponse>('/health'),
  listItems: () => request<Item[]>('/items'),
  connectionTest: () => request<ConnectionTestResponse>('/connection-test'),
  listTasks: async (filter?: TaskFilter): Promise<Task[]> => {
    const query = filter ? `?filter=${encodeURIComponent(filter)}` : '';
    const response = await request<RawTask[]>(`/tasks${query}`);
    return response.map(mapTask);
  },
  getTask: async (id: number): Promise<Task> => {
    const response = await request<RawTask>(`/tasks/${id}`);
    return mapTask(response);
  },
  createTask: async (payload: TaskCreatePayload): Promise<Task> => {
    const body = buildTaskPayload(payload);
    const response = await request<RawTask>('/tasks', {
      method: 'POST',
      body: JSON.stringify(body)
    });
    return mapTask(response);
  },
  updateTask: async (id: number, payload: TaskUpdatePayload): Promise<Task> => {
    const body = buildTaskPayload(payload);
    const response = await request<RawTask>(`/tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body)
    });
    return mapTask(response);
  },
  deleteTask: async (id: number): Promise<void> => {
    await requestVoid(`/tasks/${id}`, { method: 'DELETE' });
  },
  listProjects: async (): Promise<Project[]> => {
    const response = await request<RawProject[]>('/projects');
    return response.map(mapProject);
  },
  getProject: async (id: number): Promise<Project> => {
    const response = await request<RawProject>(`/projects/${id}`);
    return mapProject(response);
  },
  createProject: async (payload: ProjectCreatePayload): Promise<Project> => {
    const body = buildProjectPayload(payload);
    const response = await request<RawProject>('/projects', {
      method: 'POST',
      body: JSON.stringify(body)
    });
    return mapProject(response);
  },
  updateProject: async (id: number, payload: ProjectUpdatePayload): Promise<Project> => {
    const body = buildProjectPayload(payload);
    const response = await request<RawProject>(`/projects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body)
    });
    return mapProject(response);
  },
  deleteProject: async (id: number): Promise<void> => {
    await requestVoid(`/projects/${id}`, { method: 'DELETE' });
  },
  listBuckets: async (): Promise<Bucket[]> => {
    const response = await request<RawBucket[]>('/buckets');
    return response.map(mapBucket);
  },
  getBucket: async (id: number): Promise<Bucket> => {
    const response = await request<RawBucket>(`/buckets/${id}`);
    return mapBucket(response);
  },
  createBucket: async (payload: BucketCreatePayload): Promise<Bucket> => {
    const body = buildBucketPayload(payload);
    const response = await request<RawBucket>('/buckets', {
      method: 'POST',
      body: JSON.stringify(body)
    });
    return mapBucket(response);
  },
  updateBucket: async (id: number, payload: BucketUpdatePayload): Promise<Bucket> => {
    const body = buildBucketPayload(payload);
    const response = await request<RawBucket>(`/buckets/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body)
    });
    return mapBucket(response);
  },
  deleteBucket: async (id: number): Promise<void> => {
    await requestVoid(`/buckets/${id}`, { method: 'DELETE' });
  },
  createKingdomGame: async (): Promise<string> => {
    const response = await request<{ code: string }>(
      '/kingdom/games',
      { method: 'POST' }
    );
    return response.code;
  },
  joinKingdomGame: async (code: string, name: string): Promise<KingdomJoinResponse> => {
    const response = await request<{
      code: string;
      player_id: string;
      player_token: string;
    }>(`/kingdom/games/${encodeURIComponent(code)}/join`, {
      method: 'POST',
      body: JSON.stringify({ name })
    });

    return {
      code: response.code,
      playerId: response.player_id,
      playerToken: response.player_token
    };
  },
  submitKingdomWord: async (
    code: string,
    playerToken: string,
    word: string
  ): Promise<KingdomGameState> => {
    const response = await request<RawKingdomGameState>(
      `/kingdom/games/${encodeURIComponent(code)}/word`,
      {
        method: 'POST',
        body: JSON.stringify({ player_token: playerToken, word })
      }
    );

    return mapKingdomState(response);
  },
  fetchKingdomState: async (code: string, playerToken: string): Promise<KingdomGameState> => {
    const response = await request<RawKingdomGameState>(
      `/kingdom/games/${encodeURIComponent(code)}?player_token=${encodeURIComponent(playerToken)}`
    );

    return mapKingdomState(response);
  },
  startKingdomGame: async (
    code: string,
    playerToken: string
  ): Promise<KingdomGameState> => {
    const response = await request<RawKingdomGameState>(
      `/kingdom/games/${encodeURIComponent(code)}/start`,
      {
        method: 'POST',
        body: JSON.stringify({ player_token: playerToken })
      }
    );

    return mapKingdomState(response);
  },
  guessKingdomWord: async (
    code: string,
    playerToken: string,
    wordId: string,
    guessedPlayerId: string
  ): Promise<KingdomGuessResponse> => {
    const response = await request<{
      correct: boolean;
      state: RawKingdomGameState;
    }>(`/kingdom/games/${encodeURIComponent(code)}/guess`, {
      method: 'POST',
      body: JSON.stringify({
        player_token: playerToken,
        word_id: wordId,
        guessed_player_id: guessedPlayerId
      })
    });

    return {
      correct: response.correct,
      state: mapKingdomState(response.state)
    };
  },
  sortingHatPrompt: async (prompt: string): Promise<SortingHatResult> => {
    const trimmed = prompt.trim();

    return request<SortingHatResult>('/riya/sorting-hat', {
      method: 'POST',
      body: JSON.stringify({ prompt: trimmed })
    });
  },
  listSortingHatAssignments: async (): Promise<SortingHatAssignment[]> => {
    const response = await request<SortingHatAssignmentsResponse>('/riya/assignments');
    return response.assignments;
  },
  getSortingHatSelection: async (): Promise<SortingHatSelectionResponse> => {
    return request<SortingHatSelectionResponse>('/riya/controller/selection');
  },
  setSortingHatSelection: async (name: string): Promise<SortingHatSelectionResponse> => {
    const trimmed = name.trim();
    if (!trimmed) {
      throw new Error('Name is required.');
    }

    return request<SortingHatSelectionResponse>('/riya/controller/selection', {
      method: 'POST',
      body: JSON.stringify({ name: trimmed })
    });
  },
  getSortingHatState: async (): Promise<SortingHatControllerState> => {
    return request<SortingHatControllerState>('/riya/controller/state');
  },
  startSortingHat: async (): Promise<SortingHatControllerState> => {
    return request<SortingHatControllerState>('/riya/controller/start-sorting', {
      method: 'POST'
    });
  },
  restartSortingHat: async (): Promise<SortingHatControllerState> => {
    return request<SortingHatControllerState>('/riya/controller/restart', {
      method: 'POST'
    });
  },
  advanceSortingHat: async (): Promise<SortingHatControllerState> => {
    return request<SortingHatControllerState>('/riya/controller/advance', {
      method: 'POST'
    });
  },
  stepSortingHat: async (): Promise<SortingHatStepResponse> => {
    return request<SortingHatStepResponse>('/riya/sorting-hat/step', {
      method: 'POST'
    });
  },
  replaySortingHatName: async (): Promise<SortingHatSpeakResponse> => {
    return request<SortingHatSpeakResponse>('/riya/sorting-hat/replay-name', {
      method: 'POST'
    });
  },
  advanceSortingHatState: async (nextState: string): Promise<SortingHatControllerState> => {
    return request<SortingHatControllerState>(
      `/riya/sorting-hat/advance-state?next_state=${encodeURIComponent(nextState)}`,
      { method: 'POST' }
    );
  },
  completeSortingHatResponse: async (): Promise<SortingHatControllerState> => {
    return request<SortingHatControllerState>('/riya/sorting-hat/complete-response', {
      method: 'POST'
    });
  },
  speakSortingHat: async (text: string): Promise<SortingHatSpeakResponse> => {
    const trimmed = text.trim();
    if (!trimmed) {
      throw new Error('Text is required.');
    }

    return request<SortingHatSpeakResponse>('/riya/tts', {
      method: 'POST',
      body: JSON.stringify({ text: trimmed })
    });
  },
  searchTickers: async (query: string): Promise<TickerSearchResult[]> => {
    const trimmed = query.trim();
    if (!trimmed) {
      return [];
    }

    const params = new URLSearchParams({ q: trimmed });
    const response = await request<FinanceSearchResponse>(
      `/finance/options-trading/search?${params.toString()}`
    );
    return response.results;
  },
  getOptionsSnapshot: async ({
    ticker,
    expiration
  }: {
    ticker: string;
    expiration?: number;
  }): Promise<OptionsSnapshot> => {
    const trimmed = ticker.trim();
    if (!trimmed) {
      throw new Error('Ticker is required.');
    }

    const params = new URLSearchParams({ ticker: trimmed });
    if (typeof expiration === 'number' && Number.isFinite(expiration)) {
      params.set('expiration', Math.trunc(expiration).toString());
    }

    return request<FinanceSnapshotResponse>(
      `/finance/options-trading/snapshot?${params.toString()}`
    );
  },
  getPriceHistory: async ({
    ticker,
    period
  }: {
    ticker: string;
    period: string;
  }): Promise<PriceHistoryResponse> => {
    const trimmed = ticker.trim();
    if (!trimmed) {
      throw new Error('Ticker is required.');
    }

    const params = new URLSearchParams({ ticker: trimmed, period });
    return request<PriceHistoryResponse>(
      `/finance/options-trading/history?${params.toString()}`
    );
  },
  createUnifiedAccountsLinkToken: async (
    payload?: UnifiedLinkTokenRequestPayload
  ): Promise<UnifiedLinkTokenResponse> => {
    const body: Record<string, string> = {};
    if (payload?.connectionId?.trim()) {
      body.connectionId = payload.connectionId.trim();
    }
    if (payload?.oauthStateId?.trim()) {
      body.oauthStateId = payload.oauthStateId.trim();
    }
    return request<UnifiedLinkTokenResponse>('/finance/unified-accounts/link-token', {
      method: 'POST',
      body: JSON.stringify(body)
    });
  },
  exchangeUnifiedAccountsPublicToken: async (
    payload: UnifiedExchangePayload
  ): Promise<UnifiedConnectionSummary> => {
    if (!payload.publicToken?.trim()) {
      throw new Error('publicToken is required.');
    }

    return request<UnifiedConnectionSummary>('/finance/unified-accounts/exchange', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },
  getUnifiedAccounts: async (): Promise<UnifiedAccountsSnapshot> => {
    const response = await request<RawUnifiedAccountsSnapshot>('/finance/unified-accounts/accounts');
    return mapUnifiedAccountsSnapshot(response);
  },
  getUnifiedAccountTransactions: async (
    accountId: string,
    options?: { limit?: number }
  ): Promise<UnifiedTransactionsResponse> => {
    const trimmed = accountId.trim();
    if (!trimmed) {
      throw new Error('Account id is required.');
    }

    const params = new URLSearchParams();
    if (options?.limit) {
      params.set('limit', String(options.limit));
    }

    const path = params.size
      ? `/finance/unified-accounts/accounts/${encodeURIComponent(trimmed)}/transactions?${params.toString()}`
      : `/finance/unified-accounts/accounts/${encodeURIComponent(trimmed)}/transactions`;

    const response = await request<RawUnifiedTransactionsResponse>(path);
    return mapUnifiedTransactionsResponse(response);
  },
  getUnifiedAccountPositions: async (
    accountId: string
  ): Promise<UnifiedPositionsResponse> => {
    const trimmed = accountId.trim();
    if (!trimmed) {
      throw new Error('Account id is required.');
    }

    const response = await request<RawUnifiedPositionsResponse>(
      `/finance/unified-accounts/accounts/${encodeURIComponent(trimmed)}/positions`
    );
    return mapUnifiedPositionsResponse(response);
  },
  refreshUnifiedConnection: async (
    connectionId: string
  ): Promise<UnifiedConnectionSummary> => {
    const trimmed = connectionId.trim();
    if (!trimmed) {
      throw new Error('Connection id is required.');
    }

    const response = await request<{ connection: UnifiedConnectionSummary }>(
      `/finance/unified-accounts/accounts/${encodeURIComponent(trimmed)}/refresh`,
      { method: 'POST' }
    );
    return response.connection;
  },
  deleteUnifiedConnection: async (
    connectionId: string
  ): Promise<UnifiedConnectionSummary> => {
    const trimmed = connectionId.trim();
    if (!trimmed) {
      throw new Error('Connection id is required.');
    }

    return request<UnifiedConnectionSummary>(
      `/finance/unified-accounts/connections/${encodeURIComponent(trimmed)}`,
      { method: 'DELETE' }
    );
  },
  deleteUnifiedAccount: async (
    accountId: string
  ): Promise<UnifiedAccountSummary> => {
    const trimmed = accountId.trim();
    if (!trimmed) {
      throw new Error('Account id is required.');
    }

    return request<UnifiedAccountSummary>(
      `/finance/unified-accounts/accounts/${encodeURIComponent(trimmed)}`,
      { method: 'DELETE' }
    );
  }
};
